import {InputControlType, RsvpStatus} from '@wix/events-types'
import {
  NEW_FORM_FIELD_VALIDATION,
  getFormControls,
  getFormMessages,
  isDraft,
  isNoResponseEnabled,
  isTicketed,
  isWaitlistOpen,
} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useTranslation} from '@wix/yoshi-flow-editor'
import React, {useContext, useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {Field, Fields, Form as ReduxForm, submit as submitForm} from 'redux-form'
import {placeOrder} from '../../actions/placed-order'
import {useAppActions, useAppSelector} from '../../hooks/runtime'
import {getEvent} from '../../selectors/event'
import {getFormSubmitError} from '../../selectors/form-submit-errors'
import {isEditingCoupon, isEditingGiftCard, isValidatingCoupon, isValidatingGiftCard} from '../../selectors/invoice'
import {isMemberAbleToFinishRsvp} from '../../selectors/member-rsvp'
import {isRequestPending} from '../../selectors/pending-requests'
import {hasPolicies as hasPoliciesSelector} from '../../selectors/policies'
import {getReservationId} from '../../selectors/reservation'
import {getResponse} from '../../selectors/rsvp'
import {RegFormData} from '../../types/registration'
import {TicketsDetailsContext} from '../checkout/tickets-details/tickets-details-context'
import {FormSubmitButton} from '../form-submit-button'
import {Responses} from './commons/responses'
import s from './form.scss' // these styles should be overwritten by component styles
import {FormProps} from './interfaces'
import {FORM_DESKTOP_COMPONENTS} from './utils'

export const Form = ({
  submitSucceeded,
  hideButton,
  handleSubmit,
  hideAdditionalFields,
  disabledFields,
  isTicketForm,
  isLastTicket,
  onFormSubmit,
  form,
}: FormProps) => {
  const {t} = useTranslation()
  const {resetRsvpError, changeResponse} = useAppActions()
  const {setExpandedFormSubmit, resetExpandedFormSubmit} = useContext(TicketsDetailsContext)
  const dispatch = useDispatch()

  const event = useAppSelector(getEvent)
  const hasPolicies = useAppSelector(hasPoliciesSelector)
  const reservationId = useAppSelector(getReservationId)
  const response = useAppSelector(getResponse)
  const editingCoupon = useAppSelector(isEditingCoupon)
  const validatingCoupon = useAppSelector(isValidatingCoupon)
  const editingGiftCard = useAppSelector(isEditingGiftCard)
  const validatingGiftCard = useAppSelector(isValidatingGiftCard)
  const memberUpdatingRsvp = useAppSelector(isMemberAbleToFinishRsvp)
  const error = useAppSelector(state => getFormSubmitError(state, m => m))
  const submitting = useAppSelector(state => isRequestPending(state, placeOrder.typePrefix))

  const messages = getFormMessages(event)
  const waitlistOpen = isWaitlistOpen(event)
  const submitDisabled = editingCoupon || validatingCoupon || editingGiftCard || validatingGiftCard || submitting
  const formButtonVisible = !hideButton && !isTicketForm
  const ticketFormButtonVisible = !hideButton && isTicketForm && !isLastTicket
  const rsvpResponseRadioVisible = isNoResponseEnabled(event) && !reservationId && !memberUpdatingRsvp
  const formButtonText = isTicketed(event)
    ? messages.checkout.submitActionLabel || messages.rsvp.submitActionLabel
    : hasPolicies
    ? t('mobile.checkout.next')
    : messages.rsvp.submitActionLabel
  const controls = getFormControls(event) as wix.events.form.InputControl[]

  const submitFormHandler = () => {
    if (form) {
      dispatch(submitForm(form))
    }
  }

  useEffect(() => {
    setExpandedFormSubmit(submitFormHandler)

    return resetExpandedFormSubmit
  }, [form])

  useEffect(() => {
    changeResponse(waitlistOpen ? RsvpStatus.WAITING : RsvpStatus.YES)
    resetRsvpError()
  }, [])

  const submit = async (values: RegFormData) => {
    const customData = reservationId ? {reservation: reservationId} : {response}
    const data = {...customData, ...getRsvpData(values)}
    return onFormSubmit(data)
  }

  const getRsvpData = (values: RegFormData) => {
    const data = {} as RegFormData

    getControls().forEach(({name}) => {
      if (name === 'name') {
        data.firstName = values.firstName
        data.lastName = values.lastName
      } else if (name === 'email') {
        data.email = values.email.trim()
      } else if (name === 'additionalGuests') {
        data.additionalGuests = values.additionalGuests
        data.guestNames = values.additionalGuests.guestNames.map(guest => `${guest.firstName} ${guest.lastName}`)
      } else {
        data[name] = values[name]
      }
    })

    return data
  }

  const getControls = () =>
    response === RsvpStatus.YES && !hideAdditionalFields ? controls : controls.filter(control => control.system)

  const getControl = (control: wix.events.form.InputControl) => {
    const controlType = control.type || InputControlType.INPUT
    const Component = FORM_DESKTOP_COMPONENTS[controlType]
    const props: any = {
      t,
      control,
      disabled: disabledFields?.includes(control.name),
    }

    if (controlType === InputControlType.NAME) {
      return <Fields names={['firstName', 'lastName']} component={Component} props={props} />
    }

    const validate = NEW_FORM_FIELD_VALIDATION[controlType](control)

    return (
      <Field
        name={control.name}
        component={Component}
        props={props}
        validate={validate ? value => validate(value) : undefined}
      />
    )
  }

  return (
    <ReduxForm data-hook="form" className={s.form} onSubmit={handleSubmit(submit)} noValidate>
      {rsvpResponseRadioVisible && (
        <Responses response={response} onChange={changeResponse} messages={messages} waitlistOpen={waitlistOpen} />
      )}
      {getControls().map(control => (
        <div key={control.name} data-hook="form-field">
          {getControl(control)}
        </div>
      ))}
      {formButtonVisible && (
        <FormSubmitButton
          dataHook={DH.FORM_BUTTON}
          text={formButtonText}
          disabled={submitDisabled || (submitSucceeded && !error)}
          previewTooltipText={isDraft(event) ? t('draftEventPreviewTooltip') : null}
        />
      )}
      {ticketFormButtonVisible && (
        <button className={s.formButton} type="submit" data-hook={DH.NEXT_TICKET_BUTTON}>
          {t('mobile.checkout.ticket.nextTicket')}
        </button>
      )}
    </ReduxForm>
  )
}
